import {Lang, Language} from 'yougile-lang'
import {logger} from './logger'
import {Time} from './time'
import {Util} from './util'

/**
 * Implements abstraction of time intervals.
 * For example:
 *   let ti = TimeInterval.fromString('3600s');
 *   ti.seconds === 0;
 *   ti.hours === 1;
 *   ti.toSeconds() === TimeInterval.fromString('1h');
 *   ti.format() === '1:00:00';
 */
export class TimeInterval {
  private hoursPerDay = 24
  private daysPerWeek = 7

  public isNegative: boolean
  public weeks: number
  public days: number
  public hours: number
  public minutes: number
  public seconds: number

  public static isCorrectFormat(str: string): boolean {
    str = (str || '').trim().replace(/ /g, '')
    if (!str) {
      return false
    }
    const rx =
      /^([0-9]+[wн])?([0-9]+[dд])?([0-9]+[hч])?([0-9]+[mм])?([0-9]+[sс])?$/
    return !!str.match(/^[\d]+$/) || !!str.match(rx)
  }

  public static fromString(
    str: string,
    daysPerWeek = 7,
    hoursPerDay = 24
  ): TimeInterval {
    if (!str) {
      str = '0s'
    }
    str = str.trim().replace(/ /g, '')
    if (str.match(/^[\d]+$/)) {
      return new TimeInterval(false, 0, 0, +str, 0, 0, daysPerWeek, hoursPerDay)
    }
    const patt =
      /([0-9]+[wн])?([0-9]+[dд])?([0-9]+[hч])?([0-9]+[mм])?([0-9]+[sс])?/g
    Util.assert(!!str.match(patt), 'Incorrect time interval format')
    const isNeg = str.startsWith('-')
    const parts = patt.exec(str.replace('-', ''))?.slice(1) ?? []
    const [w, d, h, m, s] = parts.map((p: string) => (p ? +p.slice(0, -1) : 0))
    return new TimeInterval(isNeg, w, d, h, m, s, daysPerWeek, hoursPerDay)
  }

  public static getActual(
    oldInterval: TimeInterval,
    oldDate: number
  ): TimeInterval {
    const diff = TimeInterval.fromDates(Time.now(), oldDate)
    return oldInterval.add(diff)
  }

  public static fromDates(
    begin: number,
    end: number,
    daysPerWeek = 7,
    hoursPerDay = 24
  ): TimeInterval {
    const seconds = Math.round((end - begin) / 1000)
    return new TimeInterval(
      false,
      0,
      0,
      0,
      0,
      seconds,
      daysPerWeek,
      hoursPerDay
    )
  }

  public static fromMillis(
    millis: number,
    daysPerWeek = 7,
    hoursPerDay = 24
  ): TimeInterval {
    const seconds = Math.round(+millis / 1000)
    return new TimeInterval(
      false,
      0,
      0,
      0,
      0,
      seconds,
      daysPerWeek,
      hoursPerDay
    )
  }

  constructor(
    isNegative: boolean,
    weeks: number,
    days: number,
    hours: number,
    minutes: number,
    seconds: number,
    daysPerWeek: number,
    hoursPerDay: number
  ) {
    this.isNegative = isNegative
    this.weeks = weeks
    this.days = days
    this.hours = hours
    this.minutes = minutes
    this.seconds = seconds
    this.daysPerWeek = daysPerWeek
    this.hoursPerDay = hoursPerDay
    this.normalize()
  }

  public toSeconds(): number {
    const abs =
      this.seconds +
      this.minutes * 60 +
      this.hours * 3600 +
      this.days * this.hoursPerDay * 3600 +
      this.weeks * this.daysPerWeek * this.hoursPerDay * 3600
    return (this.isNegative ? -1 : 1) * abs
  }

  public toHours(): number {
    return Math.floor(this.toSeconds() / 3600)
  }

  public add(other: TimeInterval): TimeInterval {
    const sign = this.isNegative === other.isNegative ? 1 : -1
    if (
      this.daysPerWeek !== other.daysPerWeek ||
      this.hoursPerDay !== other.hoursPerDay
    ) {
      logger.warn('Inconsistent days/week or hours/day')
    }
    return new TimeInterval(
      this.isNegative,
      this.weeks + other.weeks * sign,
      this.days + other.days * sign,
      this.hours + other.hours * sign,
      this.minutes + other.minutes * sign,
      this.seconds + other.seconds * sign,
      this.daysPerWeek,
      this.hoursPerDay
    )
  }

  /**
   * Storing without seconds.
   */
  public toString(): string {
    const prefix = this.isNegative ? '-' : ''
    return (
      prefix +
      [
        this.weeks,
        'w',
        this.days,
        'd',
        this.hours,
        'h',
        this.minutes,
        'm',
        this.seconds,
        's'
      ].join('')
    )
  }

  public formatShort(lang: Language): string {
    const msg = Lang.getMessages(lang)
    const res: string[] = []
    if (this.isNegative) {
      res.push('-')
    }
    if (this.weeks) {
      res.push(this.weeks + msg.w)
    }
    if (this.days) {
      res.push(this.days + msg.d)
    }
    if (this.hours) {
      res.push(this.hours + msg.h)
    }
    if (this.minutes) {
      res.push(this.minutes + msg.m)
    }
    if (this.seconds) {
      res.push(this.seconds + msg.s)
    }
    return res.join('')
  }

  public format(
    noSeconds = false,
    noMinutes = false,
    fullHours = false
  ): string {
    const prefix = this.isNegative ? '-' : ''
    const parts: string[] = []
    const fullHoursValue = this.hours > 9 ? this.hours : `0${this.hours}`
    const hours = fullHours ? fullHoursValue : this.hours
    if (this.weeks) {
      parts.push(this.weeks + ' ' + Lang.msg.w + '.')
    }
    if (this.days) {
      parts.push(this.days + ' ' + Lang.msg.d + '.')
    }
    if (this.weeks === 0 && (hours || this.minutes || this.seconds)) {
      if (noSeconds) {
        if (hours) {
          parts.push(hours + ' ' + Lang.msg.h + '.')
        }
        if (!noMinutes && this.minutes) {
          parts.push(this.minutes + ' ' + Lang.msg.m + '.')
        }
      } else {
        parts.push(
          hours +
            ':' +
            ('00' + this.minutes).slice(-2) +
            ':' +
            ('00' + this.seconds).slice(-2)
        )
      }
    }
    if (parts.length === 0) {
      return '0:00:00'
    }
    return prefix + parts.join(', ')
  }

  /**
   * Restores correct bounds of the time units.
   */
  private normalize(): void {
    const upMod = (n: number, m: number) => ((n % m) + m) % m
    this.minutes += Math.floor(this.seconds / 60)
    this.seconds = upMod(this.seconds, 60)
    this.hours += Math.floor(this.minutes / 60)
    this.minutes = upMod(this.minutes, 60)
    this.days += Math.floor(this.hours / this.hoursPerDay)
    this.hours = upMod(this.hours, this.hoursPerDay)
    this.weeks += Math.floor(this.days / this.daysPerWeek)
    this.days = upMod(this.days, this.daysPerWeek)
    if (this.weeks < 0) {
      this.isNegative = !this.isNegative
      this.weeks = -this.weeks - 1
      this.days = 6 - this.days
      this.hours = 23 - this.hours
      this.minutes = 59 - this.minutes
      this.seconds = 60 - this.seconds
    }
  }
}
